// Function to get the most recent line from live_scoreboard or lines table
export const getLiveLine = (game, lineType) => {
  return game.live_scoreboard?.[lineType] ?? (lineType === 'spread' ? game.spread : game.over_under);
};

// Function to format line display
export const formatLine = (line) => {
    if (line === 0) return 'PK';
    return line > 0 ? `+${line}` : `${line}`;
};

// Function to calculate line movement
export const calculateLineMovement = (liveLine, watchLine) => {
    return liveLine - watchLine;
};

// Function to get home and away spreads
export const getSpreads = (game) => {
    const spread = getLiveLine(game, 'spread');
    return {
      homeSpread: formatLine(spread),
      awaySpread: formatLine(-spread)
    };
};

// Function to get over/under
export const getOverUnder = (game) => {
    return getLiveLine(game, 'over_under');
};

// Updated function to get the correct live line based on pick type and liveLine
export const getCorrectLiveLine = (liveLine, pickType) => {
  switch(pickType) {
    case 'home':
      return liveLine;
    case 'away':
      return -liveLine;
    case 'over':
    case 'under':
      return liveLine;
    default:
      console.error('Invalid pick type:', pickType);
      return null;
  }
};

/**
 * Pick-specific lock line for API submit.
 * watchLine and lockLine are pick-specific; liveLine is home spread (spread picks) or total (O/U).
 */
export const resolveLockLineForSubmit = (pick) => {
  if (!pick || pick.pickType === 'bye') return 0;
  if (pick.pickType === 'over' || pick.pickType === 'under') {
    return pick.lockLine ?? pick.liveLine ?? pick.watchLine;
  }
  if (pick.lockLine != null) {
    return pick.lockLine;
  }
  if (pick.watchLine != null) {
    return pick.watchLine;
  }
  return getCorrectLiveLine(pick.liveLine, pick.pickType);
};

/**
 * Normalize watch list display lines to pick-specific convention.
 * watchLine is always pick-specific; liveLine is stored as home spread (spread picks) or total (O/U).
 * Legacy mobile watches may have stored pick-specific values in both fields — avoid double-flip.
 */
export const getWatchListDisplayLines = (pick) => {
  const watchLine = pick?.watchLine;
  let liveLine = pick?.liveLine;

  if (pick?.pickType === 'over' || pick?.pickType === 'under' || pick?.pickType === 'bye') {
    return { watchLine, liveLine };
  }

  if (pick?.pickType === 'away' && watchLine != null && liveLine != null) {
    const alreadyPickSpecific =
      Math.sign(watchLine) === Math.sign(liveLine) || liveLine === 0;
    if (!alreadyPickSpecific) {
      liveLine = getCorrectLiveLine(liveLine, 'away');
    }
  }

  return { watchLine, liveLine };
};

export const getPickTeamLabel = (pick, game) => {
  if (pick?.team) return pick.team;
  if (!game) return '';
  const homeName = typeof game.home_team === 'object' ? game.home_team.name : game.home_team;
  const awayName = typeof game.away_team === 'object' ? game.away_team.name : game.away_team;
  switch (pick?.pickType) {
    case 'home': return homeName || 'Home';
    case 'away': return awayName || 'Away';
    case 'over': return 'Over';
    case 'under': return 'Under';
    default: return '';
  }
};

export const formatLockConfirmDetail = (pick, weekNumber, game) => {
  const teamLabel = getPickTeamLabel(pick, game);
  const line = resolveLockLineForSubmit(pick);
  if (pick.pickType === 'over') {
    return `Week ${weekNumber}: Over ${line}`;
  }
  if (pick.pickType === 'under') {
    return `Week ${weekNumber}: Under ${line}`;
  }
  return `Week ${weekNumber}: ${teamLabel} ${formatLine(line)}`;
};

// Function to generate formatted messages for line verification
export const formatLineVerificationMessage = (game, pickType, currentLine, lockLine) => {
  if (!game) return '';

  // For over/under picks
  if (pickType === 'over' || pickType === 'under') {
    const prefix = pickType === 'over' ? 'O' : 'U';
    const teams = `${game.away_team_abv}v${game.home_team_abv}`;
    return `${prefix}${lockLine} → ${prefix}${currentLine} ${teams}`;
  }

  // For spread picks — lockLine and currentLine are pick-specific
  const teamName = pickType === 'home' ? game.home_team : game.away_team;
  const arrow = ' → ';
  const oldLine = formatLine(lockLine);
  const newLine = formatLine(currentLine);

  return `${teamName} ${oldLine}${arrow}${newLine}`;
};

// Add these utility functions
export const calculateMargin = (homeScore, awayScore, spread) => {
  return (homeScore - awayScore) + spread;
};

export const calculateOverUnderDiff = (homeScore, awayScore, overUnder) => {
  return overUnder ? (homeScore + awayScore) - overUnder : null;
};

export const getScoreColor = (score, opposingScore) => {
  if (score >= opposingScore) return 'positiveMargin';
  if (score < opposingScore) return 'negativeMargin';
  return '';
};

// Add this utility function
export const getPickTypeDisplay = (pickType, gameOrHomeAbbr, awayAbbr) => {
  let homeAbv;
  let awayAbv;
  if (typeof gameOrHomeAbbr === 'object' && gameOrHomeAbbr !== null) {
    homeAbv = gameOrHomeAbbr.home_team_abv;
    awayAbv = gameOrHomeAbbr.away_team_abv;
  } else {
    homeAbv = gameOrHomeAbbr;
    awayAbv = awayAbbr;
  }

  switch(pickType) {
    case 'over': return 'O';
    case 'under': return 'U';
    case 'home': return homeAbv;
    case 'away': return awayAbv;
    default: return '';
  }
};
